// Phase 3.2a — Create Cycle Count dialog.
//
// Replaces the MUI <Drawer> + antd <Form> "Create Cycle Count" flow in
// cycleCount.tsx with a shadcn <Dialog>. Presentation only: the single Name
// field stays bound to the parent's `addCycleValue` state, and Save still calls
// the screen's `createOrUpdateApi` (same dispatched payload). The antd Form
// `rules` validator is ported one-to-one into a local `validate()` gate.
import * as React from "react";
import AppConstant from "../../Globals/AppConstants";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui/dialog";
import { Button } from "ui/button";
import { Input } from "ui/input";
import { Label } from "ui/label";

export interface CreateCycleCountDialogProps {
  open: boolean;
  name: string;
  onNameChange: (value: string) => void;
  /** Fires only when validation passes; runs the screen's createOrUpdateApi. */
  onSubmit: () => void;
  onCancel: () => void;
  saving: boolean;
}

export function CreateCycleCountDialog({
  open,
  name,
  onNameChange,
  onSubmit,
  onCancel,
  saving,
}: CreateCycleCountDialogProps) {
  const [error, setError] = React.useState<string>("");

  // Ported one-to-one from the legacy antd Form.Item validator.
  const validate = (): boolean => {
    if (name && name.trim() === "") {
      setError("Invalid Name");
      return false;
    }
    if (name === undefined || name === null || name === "") {
      setError("Name is required");
      return false;
    }
    setError("");
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) onSubmit();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setError("");
          onCancel();
        }
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{AppConstant?.createCycleCount}</DialogTitle>
        </DialogHeader>
        <form id="create-cycle-count-form" onSubmit={handleSubmit} className="space-y-2">
          <Label className="after:ml-0.5 after:text-destructive after:content-['*']">
            {AppConstant?.cycleCountName}
          </Label>
          <Input
            maxLength={35}
            value={name ?? ""}
            onChange={(e) => {
              onNameChange(e.target.value);
              if (error) setError("");
            }}
            autoFocus
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
        </form>
        <DialogFooter>
          <Button
            variant="outline"
            type="button"
            onClick={() => {
              setError("");
              onCancel();
            }}
          >
            {AppConstant?.cancel}
          </Button>
          <Button type="submit" form="create-cycle-count-form" disabled={saving}>
            {AppConstant?.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CreateCycleCountDialog;
